/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useState } from 'react';
import {
  Constants,
  ErrorUtilities,
  USER_ROLES,
} from '@pwc/pwc-deals-web-common';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  getFakeClientUser,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import qs from 'query-string';
import _get from 'lodash/get';
import { getCalcEngineIsProcessing } from '../../../../../common/engine/reducers/calcEngine';
import { getViewClientInfo } from '../../../../../common/helpers/viewClientVersion';
import { useApprovalWorkflow } from '../../../../../common/hooks/useApprovalWorkflow';
import {
  showModalError,
  showMoreMenuError,
} from '../../../../../common/redux/errors/errorsSlice';
import userTokenService from '../../../../../common/services/security/userTokenService';
import {
  analysesGroupsListPath,
  editAnalysisPath,
  structuredRoutes,
} from '../../../../../common/services/urlService';
import { getCommentPanelOpened } from '../../../../comments/commentsSlice';
import { getWorkflowStatus } from '../../../../workflow/workflowService';
import {
  deleteStructuredDocument,
  updateStructuredAnalysisStatus,
} from '../../../../../common/engine/services/structuredAnalysisService';
import {
  getLoadingInProcess,
  getStructuredAnalysisHasError,
  ReingestDocumentWithDependencies,
} from '../../../../../common/engine/reducers/StructuredAnalysisSlice';
import {
  getStructuredAnalysisViewDocumentsWithContentForDocument,
  getStructuredAnalysisViewHasError,
  getStructuredAnalysisViewLoadingInProcess,
  startSharing,
  sharingSucceeded,
  sharingFailed,
  selectDraftVersion,
  selectSharedVersion,
  resetStructuredAnalysis,
} from '../../../../../common/engine/reducers/structuredAnalysisViewSlice';
import { clearDataAndtransformAllIngestedStructuredDocumentsThunk } from '../../../../../common/engine/thunks/clearDataAndtransformAllIngestedStructuredDocumentsThunk';

export const useStructuredAnalysisContainer = (documentCode) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { projectId } = useParams();
  const searchParams = qs.parse(useLocation().search);
  const targetAdjustmentType = _get(searchParams, 'adjustmentType', null);
  const targetAdjustmentName = _get(searchParams, 'adjustmentName', null);
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isFakeClient = useSelector(getFakeClientUser);

  const hasIngestionError = useSelector(getStructuredAnalysisHasError);
  const hasError = useSelector(getStructuredAnalysisViewHasError);
  const [hasAlreadyLoadedHack, setHasAlreadyLoadedHack] = useState(false);

  const [isConfirmVisible, setIsConfirmVisible] = useState(false);
  const isCommentPanelOpen = useSelector(getCommentPanelOpened);
  const isProcessing = useSelector(getCalcEngineIsProcessing);
  const reIngestingDocuments = useSelector(getLoadingInProcess);
  const initialViewingAsClient = !isPwcUser;
  const [viewingAsClient, setViewingAsClient] = useState(
    initialViewingAsClient
  );
  const initializingData = useSelector(
    getStructuredAnalysisViewLoadingInProcess
  );

  const analysis = useSelector(
    getStructuredAnalysisViewDocumentsWithContentForDocument(documentCode)
  );

  const draftVersion = useSelector(selectDraftVersion(documentCode));
  const sharedVersion = useSelector(selectSharedVersion(documentCode));

  const [chartKey, setChartKey] = useState(1);

  useEffect(() => {
    setViewingAsClient(initialViewingAsClient);
    setChartKey(1);
    setHasAlreadyLoadedHack(false);
  }, [dispatch, isPwcUser, initialViewingAsClient]);

  const incrementChartKey = useCallback(
    () => setChartKey((chartKeyPrevState) => chartKeyPrevState + 1),
    [setChartKey]
  );

  useEffect(() => {
    incrementChartKey();
  }, [isCommentPanelOpen, incrementChartKey]);

  const fetchAnalysis = useCallback(async () => {
    await dispatch(
      clearDataAndtransformAllIngestedStructuredDocumentsThunk(
        projectId,
        documentCode,
        viewingAsClient
      )
    );
    setHasAlreadyLoadedHack(true);
  }, [dispatch, documentCode, projectId, viewingAsClient]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis, isFakeClient]);

  const showClientVersion = () => {
    if (!isProcessing) {
      setViewingAsClient(true);
    }
  };

  const showDraftVersion = () => {
    if (!isProcessing) {
      setViewingAsClient(false);
    }
  };

  const viewClient = {
    ...getViewClientInfo(
      showClientVersion,
      showDraftVersion,
      analysis && analysis.versions,
      analysis && analysis.content.versionNumber,
      analysis && analysis.content.versionStatus
    ),
    versionData: {
      draftVersion: {
        ...draftVersion,
      },
      sharedVersion: {
        ...sharedVersion,
      },
    },
  };

  const handleEdit = () => {
    history.push(editAnalysisPath(projectId, structuredRoutes[documentCode]));
  };

  const handleDelete = async () => {
    try {
      await deleteStructuredDocument(projectId, documentCode);
      setIsConfirmVisible(false);
      history.push(analysesGroupsListPath(projectId));
    } catch (e) {
      setIsConfirmVisible(false);
      dispatch(
        showModalError(Constants.MoreMenuErrorType.UNABLE_TO_SAVE_CHANGE)
      );
    }
  };

  const handleRefresh = async () => {
    setViewingAsClient(initialViewingAsClient);
    await dispatch(ReingestDocumentWithDependencies(projectId, documentCode));
    await fetchAnalysis();
  };

  const versions = analysis ? analysis.versions : [];

  const workflowActions = useApprovalWorkflow(
    getWorkflowStatus,
    async (_projectId, contentId, step) => {
      try {
        dispatch(startSharing());
        await updateStructuredAnalysisStatus(
          _projectId,
          analysis?.content.contentThreadId,
          step
        );
        dispatch(sharingSucceeded());
      } catch (e) {
        dispatch(sharingFailed());
        if (ErrorUtilities.hasLibraryItemCorruptedError(e)) {
          dispatch(
            showMoreMenuError({
              itemId: contentId,
              moreMenuErrorType:
                Constants.MoreMenuErrorType.LIBRARY_ITEM_CORRUPTED,
            })
          );
        } else {
          dispatch(
            showMoreMenuError({
              itemId: contentId,
              moreMenuErrorType:
                Constants.MoreMenuErrorType.UNABLE_TO_SAVE_CHANGE,
            })
          );
        }
        return;
      }
      await fetchAnalysis();
    },
    projectId,
    analysis && analysis.content.contentId,
    analysis && analysis.content,
    versions
  );

  const isClientView = !isPwcUser || viewClient.isOnClientVersion;
  const hasLoadingError = hasError || hasIngestionError;
  const isLoading =
    !hasAlreadyLoadedHack ||
    initializingData ||
    isProcessing ||
    reIngestingDocuments.length > 0;

  const analysisContent = _get(analysis, 'content', undefined);

  return {
    dispatch,
    projectId,
    targetAdjustmentType,
    targetAdjustmentName,
    hasError: hasLoadingError,
    analysis: analysisContent,
    isConfirmVisible,
    setIsConfirmVisible,
    isLoading,
    versions,
    chartKey,
    incrementChartKey,
    viewClient,
    handleEdit,
    handleDelete,
    handleRefresh,
    workflowActions,
    isClientView,
  };
};
